'use client'

/**
 * Portalled hover/focus card for the Due-Diligence cockpit. Renders the tooltip into document.body with
 * fixed positioning so it floats ABOVE everything and can never be clipped by an ancestor's overflow or
 * a lower stacking context — the bug on /chart where the pure-CSS absolute card was cut off by the rail
 * (finding chart-hovertip-clipping-0914). Position is measured from the trigger on open, flips above when
 * near the bottom, and clamps to the viewport. Content is plain data passed as props (server-safe).
 */
import { useCallback, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

type Fact = { k: string; v: string }
const TIP_W = 288 // matches w-72

export default function Hovertip({ children, title, plain, why, facts, triggerClass, tabIndex = 0 }: {
  children: React.ReactNode; title: string; plain?: string; why?: string; facts?: Fact[]; triggerClass?: string; tabIndex?: number
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number; above: boolean } | null>(null)

  const place = useCallback(() => {
    const el = ref.current
    if (!el || typeof window === 'undefined') return
    const r = el.getBoundingClientRect()
    const margin = 10
    const left = Math.min(Math.max(margin, r.left), window.innerWidth - TIP_W - margin)
    const above = window.innerHeight - r.bottom < 240 // flip up near the bottom
    const top = above ? r.top - 8 : r.bottom + 8
    setPos({ top, left, above })
  }, [])
  const show = useCallback(() => { place(); setOpen(true) }, [place])
  const hide = useCallback(() => setOpen(false), [])

  return (
    <span ref={ref} tabIndex={tabIndex} className={triggerClass} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
      {children}
      {open && pos && typeof document !== 'undefined' && createPortal(
        <span
          role="tooltip"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: TIP_W, transform: pos.above ? 'translateY(-100%)' : 'none', zIndex: 2147483000 }}
          className="pointer-events-none block max-w-[calc(100vw-1.25rem)] rounded-xl border border-border bg-card p-3 text-left shadow-2xl dark:shadow-black/70"
        >
          <span className="block text-[12px] font-bold text-foreground">{title}</span>
          {plain && <span className="mt-1 block text-[12px] leading-relaxed text-foreground/90">{plain}</span>}
          {facts && facts.length > 0 && (
            <span className="mt-2 block space-y-0.5">
              {facts.map((f) => (
                <span key={f.k} className="flex justify-between font-mono text-[11px]"><span className="text-muted-foreground">{f.k}</span><span className="text-foreground/90">{f.v}</span></span>
              ))}
            </span>
          )}
          {why && <span className="mt-2 block border-t border-border pt-2 text-[11.5px] leading-relaxed text-muted-foreground"><span className="font-semibold text-amber-600 dark:text-amber-400">Why it matters: </span>{why}</span>}
        </span>,
        document.body,
      )}
    </span>
  )
}
